<template>
  <div class="assessment-ai-chat flex flex-col min-h-0 flex-1">
    <div
      v-if="loading"
      class="flex-1 flex items-center justify-center py-12"
    >
      <ProgressSpinner />
    </div>

    <div
      v-else-if="error && !question"
      class="p-4 bg-red-50 border border-red-200 rounded-lg"
    >
      <p class="text-red-800">{{ error }}</p>
      <Button class="mt-3" :label="$t('AssessmentAiChat.retry')" @click="initialize" />
    </div>

    <template v-else>
      <div class="ai-chat-header shrink-0 mb-4">
        <p class="text-sm text-gray-600">
          {{ $t('AssessmentAiChat.questionProgress', {
            current: (question?.index ?? 0) + 1,
            total: question?.totalQuestions ?? 0,
          }) }}
        </p>
        <p v-if="displayQuestionText" class="text-lg font-medium text-gray-900 mt-2 leading-snug">
          {{ displayQuestionText }}
        </p>
      </div>

      <div v-if="requiresManualInput" class="manual-fallback flex-1 min-h-0 overflow-y-auto">
        <p class="text-sm text-gray-600 mb-4">
          {{ $t('AssessmentAiChat.manualFallbackHint') }}
        </p>
        <component :is="questionType" />
        <div class="mt-4 flex justify-end">
          <Button
            :label="$t('AssessmentAiChat.continueAfterManual')"
            icon="pi pi-arrow-right"
            :loading="continuingManual"
            @click="continueAfterManual"
          />
        </div>
      </div>

      <template v-else>
        <div class="answer-card shrink-0 p-5 border border-orange-200 rounded-xl bg-orange-50/40 mb-4">
          <p class="text-sm text-gray-700 mb-4">
            {{ question?.type === 'rangeSlider'
              ? $t('AssessmentAiChat.directSliderHint')
              : $t('AssessmentAiChat.directTextHint') }}
          </p>

          <div v-if="question?.type === 'rangeSlider'" class="proposal-slider mb-5">
            <input
              v-model.number="proposedValue"
              type="range"
              min="1"
              max="5"
              step="1"
              class="w-full"
              :aria-label="$t('AssessmentAiChat.adjustRating')"
            />
            <div class="flex justify-between text-xs text-gray-500 mt-2">
              <span>1 — {{ $t('SliderQuestion.datalistOptions.stronglyDisagree') }}</span>
              <span class="font-semibold text-orange-700 text-base">{{ proposedValue }}</span>
              <span>5 — {{ $t('SliderQuestion.datalistOptions.stronglyAgree') }}</span>
            </div>
          </div>

          <Textarea
            v-else-if="question?.type === 'textField'"
            v-model="proposedValue"
            rows="3"
            class="w-full mb-4"
            :placeholder="$t('AssessmentAiChat.textPlaceholder')"
          />

          <div class="flex flex-wrap gap-2">
            <Button
              :label="$t('AssessmentAiChat.confirmAnswer')"
              icon="pi pi-check"
              class="btn-primary-accent"
              :loading="confirming"
              :disabled="!canConfirm"
              @click="handleConfirm"
            />
          </div>
        </div>

        <div class="shrink-0 mb-2">
          <Button
            type="button"
            :label="showHelpChat ? $t('AssessmentAiChat.hideHelp') : $t('AssessmentAiChat.showHelp')"
            severity="secondary"
            text
            size="small"
            @click="showHelpChat = !showHelpChat"
          />
        </div>

        <div
          v-if="showHelpChat"
          class="help-section flex flex-col min-h-0 flex-1"
        >
          <div
            ref="messagesContainer"
            class="flex-1 overflow-y-auto space-y-3 mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200 max-h-[200px]"
          >
            <div
              v-for="(message, index) in messages"
              :key="index"
              class="flex"
              :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
            >
              <div
                class="max-w-[90%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap"
                :class="message.role === 'user'
                  ? 'bg-violet-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-800'"
              >
                {{ message.content }}
              </div>
            </div>
            <div v-if="isStreaming" class="flex justify-start">
              <div class="bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 max-w-[90%]">
                {{ streamingText }}<span class="animate-pulse">▍</span>
              </div>
            </div>
          </div>

          <form
            class="shrink-0 flex gap-2"
            @submit.prevent="handleSend"
          >
            <InputText
              v-model="draft"
              class="flex-1"
              :placeholder="$t('AssessmentAiChat.helpPlaceholder')"
              :disabled="isStreaming || sending"
              autocomplete="off"
            />
            <Button
              type="submit"
              icon="pi pi-send"
              :loading="sending || isStreaming"
              :disabled="!draft.trim() || isStreaming"
              :aria-label="$t('AssessmentAiChat.send')"
            />
          </form>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import Textarea from 'primevue/textarea';
import NetworkAssessment from '@/helpers/networking/NetworkAssessment';
import { isConfirmIntent, parseRatingFromMessage } from '@/helpers/assessmentAssistParsing';
import SliderQuestion from '@/components/Home/QuestionnaireComponent/SliderQuestion.vue';
import TextQuestion from '@/components/Home/QuestionnaireComponent/TextQuestion.vue';

export default {
  name: 'AssessmentAiChat',
  components: {
    Button,
    InputText,
    ProgressSpinner,
    Textarea,
    SliderQuestion,
    TextQuestion,
  },
  props: {
    assessmentId: {
      type: String,
      required: true,
    },
  },
  emits: ['completed'],
  data() {
    return {
      loading: true,
      sending: false,
      confirming: false,
      continuingManual: false,
      isStreaming: false,
      streamingText: '',
      error: null,
      messages: [],
      draft: '',
      question: null,
      requiresManualInput: false,
      proposedValue: null,
      showHelpChat: false,
    };
  },
  computed: {
    questionType() {
      return this.$store.getters.getSelectedQuestionType;
    },
    displayQuestionText() {
      if (this.question?.text?.trim()) {
        return this.question.text;
      }
      const selected = this.$store.state.questionnaireManagement.selectedQuestion;
      const locale = this.$i18n.locale;
      const raw = selected?.properties?.content?.text_question?.[locale]
        ?? selected?.properties?.content?.text_question?.de
        ?? selected?.properties?.content?.text_question?.en
        ?? '';
      return raw.trim();
    },
    canConfirm() {
      if (this.question?.type === 'rangeSlider') {
        const n = Number(this.proposedValue);
        return !Number.isNaN(n) && n >= 1 && n <= 5;
      }
      if (this.question?.type === 'textField') {
        return typeof this.proposedValue === 'string' && this.proposedValue.trim().length > 0;
      }
      return false;
    },
  },
  mounted() {
    this.initialize();
  },
  methods: {
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    prepareAnswerCard() {
      if (this.question?.type === 'rangeSlider') {
        this.proposedValue = 3;
      } else if (this.question?.type === 'textField') {
        this.proposedValue = '';
      }
    },
    syncQuestionToStore(index) {
      if (index == null) return;
      this.$store.commit('setQuestionnaireIndex', index);
      this.$store.commit('setSelectedQuestion');
      this.$store.commit('setSelectedQuestionType');
      this.$store.commit('initAnswer');
      this.$store.commit('setSelectedQuestionProgressText');
      this.$store.commit('setSelectedQuestionChapterText');
    },
    applySessionData(data) {
      this.question = data.question ?? this.question;
      this.requiresManualInput = Boolean(data.requiresManualInput);
      this.messages = [...(data.messages ?? [])];
      const opening = data.openingMessage || data.message;
      if (opening && !this.messages.some((m) => m.content === opening)) {
        this.messages.unshift({ role: 'assistant', content: opening });
      }
      if (this.question?.index != null) {
        this.syncQuestionToStore(this.question.index);
      }
      if (!this.requiresManualInput) {
        this.prepareAnswerCard();
      }
    },
    async initialize() {
      this.loading = true;
      this.error = null;
      this.messages = [];
      this.showHelpChat = false;

      const currentIndex = this.$store.getters.getQuestionnaireIndex;
      if (typeof currentIndex === 'number') {
        this.syncQuestionToStore(currentIndex);
      }

      try {
        const locale = this.$i18n.locale === 'de' ? 'de' : 'en';
        const data = await NetworkAssessment.startAssist(this.assessmentId, locale);
        if (!data?.question) {
          throw new Error(this.$t('AssessmentAiChat.startFailed'));
        }
        this.applySessionData(data);
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || this.$t('AssessmentAiChat.startFailed');
      } finally {
        this.loading = false;
      }
    },
    async handleSend() {
      const content = this.draft.trim();
      if (!content || this.sending) return;

      const rating = parseRatingFromMessage(content);
      if (rating !== null && this.question?.type === 'rangeSlider') {
        this.proposedValue = rating;
        this.messages.push({ role: 'user', content });
        this.messages.push({
          role: 'assistant',
          content: this.$t('AssessmentAiChat.ratingAck', { value: rating }),
        });
        this.draft = '';
        this.scrollToBottom();
        return;
      }

      if (isConfirmIntent(content) && this.canConfirm) {
        this.draft = '';
        await this.handleConfirm();
        return;
      }

      if (this.question?.type === 'textField' && content.length > 0) {
        this.proposedValue = content;
      }

      this.messages.push({ role: 'user', content });
      this.draft = '';
      this.sending = true;
      this.isStreaming = true;
      this.streamingText = '';
      this.scrollToBottom();

      try {
        await NetworkAssessment.sendAssistMessageStream(this.assessmentId, content, {
          onToken: (token) => {
            this.streamingText += token;
            this.scrollToBottom();
          },
          onDone: (result) => {
            if (result.suggestedValue != null && this.question?.type === 'rangeSlider') {
              this.proposedValue = result.suggestedValue;
            }
            if (this.streamingText || result.message) {
              this.messages.push({
                role: 'assistant',
                content: result.message || this.streamingText,
              });
            }
            this.streamingText = '';
            this.isStreaming = false;
            this.scrollToBottom();
          },
          onError: (payload) => {
            throw new Error(payload?.message || 'Stream failed');
          },
        });
      } catch (err) {
        this.messages.push({
          role: 'assistant',
          content: err?.message || this.$t('AssessmentAiChat.messageFailed'),
        });
        this.streamingText = '';
        this.isStreaming = false;
      } finally {
        this.sending = false;
      }
    },
    async handleConfirm() {
      if (!this.canConfirm) return;
      this.confirming = true;
      const savedQuestionIndex = this.question?.index;
      const valueToSave = this.question?.type === 'textField'
        ? this.proposedValue.trim()
        : Number(this.proposedValue);
      try {
        const data = await NetworkAssessment.confirmAssist(this.assessmentId, valueToSave);
        await this.$store.dispatch('syncAfterAiConfirm', {
          questionIndex: data.savedQuestionIndex ?? savedQuestionIndex,
          savedAnswer: data.savedAnswer ?? { value: valueToSave },
          progress: data.progress,
          status: data.status,
          savedUntilQuestion: data.savedUntilQuestion,
          completed: Boolean(data.completed),
          nextIndex: data.completed ? undefined : data.questionIndex,
        });

        if (data.completed) {
          this.$toast?.add?.({
            severity: 'success',
            summary: this.$t('AssessmentAiChat.submittedTitle'),
            detail: this.$t('AssessmentAiChat.submittedDetail'),
            life: 5000,
          });
          this.$emit('completed');
          return;
        }

        this.showHelpChat = false;
        this.applySessionData(data);
        this.scrollToBottom();
      } catch (err) {
        this.$toast?.add?.({
          severity: 'error',
          summary: this.$t('AssessmentAiChat.confirmFailed'),
          detail: err?.response?.data?.message || err?.message || '',
          life: 4000,
        });
      } finally {
        this.confirming = false;
      }
    },
    async continueAfterManual() {
      this.continuingManual = true;
      try {
        await this.$store.dispatch('setNextQuestion');
        await this.initialize();
      } catch (err) {
        this.$toast?.add?.({
          severity: 'error',
          summary: this.$t('AssessmentAiChat.manualContinueFailed'),
          detail: err?.message || '',
          life: 4000,
        });
      } finally {
        this.continuingManual = false;
      }
    },
  },
};
</script>

<style scoped>
.assessment-ai-chat {
  width: 100%;
}

.proposal-slider input[type='range'] {
  accent-color: var(--color-orange, #e4821a);
  height: 2rem;
}
</style>
